import React, { FunctionComponent, MouseEvent } from 'react';
import GenericTable from 'components/Table/GenericTable';
import { TableRowData, TableColumn, TableColumn$RenderProps, RowActionsRender } from '@inplayer-org/inplayer-ui';
import styled from 'styled-components';
import moment from 'moment';

// utils
import { convertArrayToCamelCase } from 'utils/helpers';
import media from 'utils/mediaQueries';

// types
import { Codes, CodeType, CodeDetails } from 'ducks/merchantDucks/items/types';

// components
import CommonIcon from 'components/CommonIcon';

const StyledSpan = styled.span`
  vertical-align: text-top;
`;

export const CodesTableWrapper = styled.div`
  table {
    ${media.customMaxWidth(1240)} {
      table-layout: fixed;
    }
  }
`;

interface OwnProps {
  codes: Codes;
  handleShowModal: (code: string) => void;
  selectCode: (code: CodeType) => void;
  handlePageClick: (page: number) => any;
  paginationPage: number;
  renderHeaderSection: () => Node | JSX.Element;
}

type Props = OwnProps;

interface CodesTableData extends TableRowData {
  id: number;
  code: string;
  startDate: number;
  endDate: number;
  type: string;
  concurrentSessions: number;
}

const CodesTable: FunctionComponent<Props> = ({
  codes: { collection, total, limit, isFetching },
  handleShowModal,
  selectCode,
  handlePageClick,
  paginationPage,
  renderHeaderSection,
}) => {
  const codesCollection = convertArrayToCamelCase<CodeDetails, CodesTableData>(collection);

  const generateColumns = (): Array<TableColumn<CodesTableData>> => [
    {
      title: 'Passcode',
      key: 'code',
    },
    {
      title: 'Start date (UTC)',
      key: 'startDate',
      render: ({ value }: TableColumn$RenderProps<CodesTableData>) =>
        moment(value * 1000)
          .utc()
          .format('YYYY/MM/DD HH:mm'),
    },
    {
      title: 'End date (UTC)',
      key: 'endDate',
      render: ({ value }: TableColumn$RenderProps<CodesTableData>) =>
        moment(value * 1000)
          .utc()
          .format('YYYY/MM/DD HH:mm'),
    },
    {
      title: 'Passcode type',
      key: 'type',
    },
    {
      title: 'Simultaneous uses',
      key: 'concurrentSessions',
    },
  ];

  const handleEditCode = (row: CodesTableData) => {
    selectCode(row);
  };

  const renderRowActions: RowActionsRender<CodesTableData> = ({ row }) => {
    const handleOpenCodeDetails = (e: MouseEvent<HTMLElement>) => {
      e.preventDefault();
      handleEditCode(row);
    };

    const showModal = (e: MouseEvent<HTMLElement>) => {
      e.preventDefault();
      handleShowModal(row.code);
    };

    return (
      <>
        <StyledSpan key={row.id}>
          <CommonIcon name="edit" hoverStatus="edit" onClick={handleOpenCodeDetails} />
        </StyledSpan>
        {/* TODO: Uncomment the code lines when delete functionality is implemented on BE */}
        {/* <StyledSpan id={row.code}>
          <CommonIcon name="delete" hoverStatus="delete" onClick={showModal} />
        </StyledSpan> */}
      </>
    );
  };

  return (
    <CodesTableWrapper>
      <GenericTable<CodesTableData>
        hasBottomPagination
        columns={generateColumns()}
        data={codesCollection}
        total={total}
        handlePageClick={handlePageClick}
        currentPage={paginationPage}
        showLoader={isFetching}
        pageRangeDisplayed={3}
        itemsPerPage={limit}
        options={{
          rowActions: renderRowActions,
          headerSection: renderHeaderSection(),
        }}
        type="passcodes"
        actionsRowTitle="Edit"
      />
    </CodesTableWrapper>
  );
};
export default CodesTable;
